// ... other imports remain unchanged
import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MobileHeader from './MobileHeader';
import PageHeader from './PageHeader';
import MainTableV2 from './MainTableV2';
import AdminCardOrderForm from './AdminCardOrderForm';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

const CardOrders = () => {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const primaryColor = theme.primary;
  const apiURL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const cardGroups = useSelector((state) => state.policies.cardGroups);
  const policies = useSelector((state) => state.policies.policies);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdminOrderForm, setShowAdminOrderForm] = useState(false);

  // New filter states
  const [fulfilledFilter, setFulfilledFilter] = useState('all'); // 'all', 'fulfilled', 'unfulfilled'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const clearAlert = () => setAlertMessage(null);

  // Fetch and sort orders by CreatedAt descending
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiURL}/dataServer?`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'getCardOrders',
          payload: {
            userId: currentUser?.userId
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        const sortedOrders = result.sort(
          (a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt)
        );
        setData(sortedOrders);
      } else {
        setAlertMessage(`Server Error: ${await response.text()}`);
      }
    } catch (error) {
      setAlertMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.userId) {
      fetchData();
    }
  }, [currentUser]);

  const handleEditClick = (order) => {
    navigate('/editcardorder', { state: { orderId: order.OrderId } });
  };

  const handleAdminOrderSuccess = () => {
    fetchData(); // Refresh the data after successful order
    setAlertMessage('Successfully submitted admin card order!');
  };

  const columns = useMemo(() => [
    {
      name: 'Order ID',
      selector: (row) => row.OrderId || 'N/A',
      sortable: true,
      reorder: true,
      style: { width: '140px', whiteSpace: 'nowrap' }
    },
    {
      name: 'Created',
      selector: (row) =>
        moment(row.CreatedAt)
          .tz('America/Chicago')
          .format('MM/DD/YYYY'),
      sortable: true,
      reorder: true,
      style: { width: '180px', whiteSpace: 'nowrap' }
    },
    {
      name: 'Account',
      selector: (row) => {
        if (row.CardGroupId && cardGroups?.length) {
          const foundGroup = cardGroups.find(
            (g) => Number(g.cardGroupID) === Number(row.CardGroupId)
          );
          if (foundGroup) {
            return `${foundGroup.groupName} (${foundGroup.AccountNumber})`;
          }
        }
        if (row.PolicyNumber && policies?.length) {
          const foundPolicy = policies.find(
            (p) => Number(p.PolicyNumber) === Number(row.PolicyNumber)
          );
          if (foundPolicy) {
            return `${foundPolicy.CompanyName || foundPolicy.Description} (${foundPolicy.PolicyNumber})`;
          }
        }
        return 'N/A';
      },
      sortable: true,
      reorder: true,
      width: '250px'
    },
    {
      name: 'Address',
      selector: (row) => {
        const { AddressLine1, AddressLine2, City, State, Zip } = row;
        return [AddressLine1, AddressLine2, City, State, Zip].filter(Boolean).join(', ');
      },
      sortable: false,
      reorder: true,
      wrap: true
    },
    {
      name: 'Embossed Name',
      selector: (row) =>
        row.SubDomain ? row.groupName : (row.EmbossedNameOnCards || 'N/A'),
      sortable: true,
      reorder: true,
      wrap: true
    },
    {
      name: '# of Cards',
      selector: (row) => row.NumberOfCards || 0,
      sortable: true,
      reorder: true,
      style: { width: '120px', whiteSpace: 'nowrap' }
    },
    {
      name: 'Fulfilled',
      selector: (row) => (row.Fulfilled ? 'Yes' : 'No'),
      sortable: true,
      reorder: true,
      style: { width: '100px', whiteSpace: 'nowrap' }
    }
    // Actions column removed per current configuration
  ], [cardGroups, policies]);

  const filterConfig = [
    { key: 'OrderId', type: 'string' },
    { key: 'EmbossedNameOnCards', type: 'string' },
    { key: 'groupName', type: 'string' },
    { key: 'City', type: 'string' },
    { key: 'State', type: 'string' },
    { key: 'Zip', type: 'string' },
    { key: 'AddressLine1', type: 'string' },
    { key: 'CreatedAt', type: 'date' }
  ];

  // Apply additional filters: fulfillment and date range
  const filteredData = useMemo(() => {
    let filtered = data;
    if (fulfilledFilter !== 'all') {
      filtered = filtered.filter(order =>
        fulfilledFilter === 'fulfilled' ? order.Fulfilled : !order.Fulfilled
      );
    }
    if (startDate) {
      filtered = filtered.filter(order => new Date(order.CreatedAt) >= new Date(startDate));
    }
    if (endDate) {
      filtered = filtered.filter(order => new Date(order.CreatedAt) <= new Date(endDate));
    }

    return filtered;
  }, [data, fulfilledFilter, startDate, endDate]);

  // Calculate metrics based on filteredData
  const orderMetrics = useMemo(() => {
    const fulfilledOrders = filteredData.filter(order => order.Fulfilled);
    const openOrders = filteredData.filter(order => !order.Fulfilled);
    const totalFulfilledOrders = fulfilledOrders.length;
    const totalOpenOrders = openOrders.length;
    const totalCardsFulfilled = fulfilledOrders.reduce(
      (sum, order) => sum + (order.NumberOfCards || 0),
      0
    );
    const totalCardsOpen = openOrders.reduce(
      (sum, order) => sum + (order.NumberOfCards || 0),
      0
    );
    return { totalFulfilledOrders, totalOpenOrders, totalCardsFulfilled, totalCardsOpen };
  }, [filteredData]);

  const ordersChartData = useMemo(() => ({
    labels: ['Fulfilled', 'Open'],
    datasets: [
      {
        label: 'Number of Orders',
        data: [orderMetrics.totalFulfilledOrders, orderMetrics.totalOpenOrders],
        backgroundColor: [primaryColor, '#ccc']
      }
    ]
  }), [orderMetrics, primaryColor]);

  const cardsChartData = useMemo(() => ({
    labels: ['Fulfilled', 'Open'],
    datasets: [
      {
        label: 'Number of Cards',
        data: [orderMetrics.totalCardsFulfilled, orderMetrics.totalCardsOpen],
        backgroundColor: [primaryColor, '#ccc']
      }
    ]
  }), [orderMetrics, primaryColor]);

  return (
    <div className="mobile-container mx-auto px-4 sm:px-6 lg:px-8">
      <MobileHeader theme={theme} />
      <PageHeader
        heading="Card Orders"
        context="Review all card orders placed by users."
      />

      {/* Modern Analytics Dashboard */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        {/* Header with Action Button */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Card Orders Analytics</h2>
          {currentUser?.isAdmin && (
            <button
              onClick={() => setShowAdminOrderForm(true)}
              style={{ backgroundColor: primaryColor }}
              className="px-4 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all shadow-sm hover:shadow-md"
            >
              + New Order
            </button>
          )}
        </div>

        {/* Compact Metrics Grid */}
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium">Fulfilled Orders</p>
                  <p className="text-2xl font-bold text-green-700">{orderMetrics.totalFulfilledOrders}</p>
                </div>
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-600 text-sm font-medium">Open Orders</p>
                  <p className="text-2xl font-bold text-red-700">{orderMetrics.totalOpenOrders}</p>
                </div>
                <div className="w-8 h-8 bg-red-200 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-medium">Cards Fulfilled</p>
                  <p className="text-2xl font-bold text-blue-700">{orderMetrics.totalCardsFulfilled}</p>
                </div>
                <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-600 text-sm font-medium">Cards Pending</p>
                  <p className="text-2xl font-bold text-orange-700">{orderMetrics.totalCardsOpen}</p>
                </div>
                <div className="w-8 h-8 bg-orange-200 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Compact Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Order Status Distribution</h3>
              <div className="h-40">
                <Bar
                  data={ordersChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        cornerRadius: 8
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: { display: false },
                        ticks: { font: { size: 11 } }
                      },
                      x: {
                        grid: { display: false },
                        ticks: { font: { size: 11 } }
                      }
                    }
                  }}
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Card Volume Distribution</h3>
              <div className="h-40">
                <Bar
                  data={cardsChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        cornerRadius: 8
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: { display: false },
                        ticks: { font: { size: 11 } }
                      },
                      x: {
                        grid: { display: false },
                        ticks: { font: { size: 11 } }
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {/* Compact Filters */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={fulfilledFilter}
                  onChange={(e) => setFulfilledFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="all">All Orders</option>
                  <option value="fulfilled">Fulfilled Only</option>
                  <option value="unfulfilled">Open Only</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <MainTableV2
        columns={columns}
        data={filteredData}
        primaryColor={primaryColor}
        loading={loading}
        alertMessage={alertMessage}
        clearAlert={clearAlert}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterConfig={filterConfig}
      >
        {/* Optional action button can go here */}
      </MainTableV2>

      <AdminCardOrderForm
        isOpen={showAdminOrderForm}
        onClose={() => setShowAdminOrderForm(false)}
        onSuccess={handleAdminOrderSuccess}
      />
    </div>
  );
};

export default CardOrders;
